import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Session } from "./models/Session";
import { LoadingStatus } from "./models/LoadingStatus";
import { SessionsState } from "./models/SessionsState";
import { ListViewState } from "./models/ListViewState";
import { PaginatedResult } from "./contracts/PaginatedResult";
import { DateFilter } from "../../utils/date-utils";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import {
  fetchSessionById,
  createSession,
  updateSession,
  addInviteeToSession,
  removeInviteeFromSession,
  fetchSessionsPaginated,
  fetchSessionsByDateRangePaginated,
  fetchAvailableTimeSlotsForNextWeek,
  fetchUpcomingSessions,
  fetchRecentlyCompletedSessions,
} from "./slice-extensions/sessions-slice-extension";
import { MAX_DISPLAYED_SESSIONS } from "./constants/sessionConstants";

/**
 * Initial state for the sessions slice
 */
const initialState: SessionsState = {
  initializationStatus: "not-initialized",
  sessions: [],
  currentSession: undefined,
  status: LoadingStatus.Idle,
  statusMessage: undefined,
  lastError: undefined,
  pagination: {
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  },
  timeSlots: {
    availableTimeSlots: [],
    initializationStatus: "not-initialized",
    statusMessage: undefined,
  },
  upcomingSessionsState: {
    upcomingSessions: [],
    loadingStatus: LoadingStatus.Idle,
    initializationStatus: "not-initialized",
  },
  recentlyCompletedSessionsState: {
    recentlyCompletedSessions: [],
    loadingStatus: LoadingStatus.Idle,
    initializationStatus: "not-initialized",
  },
  listViewState: {
    selectedTab: "upcoming",
    currentPage: 1,
    dateFilter: DateFilter.All,
  },
};

/**
 * Sessions slice containing reducers and actions
 */
const sessionsSlice = createSlice({
  name: "sessions",
  initialState,
  reducers: {
    /**
     * Clears any error messages from the state
     */
    clearError: (state) => {
      state.statusMessage = undefined;
      if (state.status === LoadingStatus.Failed) {
        state.status = LoadingStatus.Idle;
      }
    },
    /**
     * Sets the current session for create/edit operations
     */
    setCurrentSession: (state, action: PayloadAction<Session | undefined>) => {
      state.currentSession = action.payload;
    },
    /**
     * Clears the current session
     */
    clearCurrentSession: (state) => {
      state.currentSession = undefined;
    },
    /**
     * Resets the sessions state to initial state
     */
    resetSessions: () => initialState,
    /**
     * Sets the selected tab in the SessionsList view
     */
    setSelectedTab: (
      state,
      action: PayloadAction<"upcoming" | "completed">
    ) => {
      state.listViewState.selectedTab = action.payload;
      // Reset to first page when switching tabs
      state.listViewState.currentPage = 1;
    },
    /**
     * Sets the current page in the SessionsList view
     */
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.listViewState.currentPage = action.payload;
    },
    /**
     * Sets the date filter in the SessionsList view (synchronized across both tabs)
     */
    setDateFilter: (state, action: PayloadAction<DateFilter>) => {
      state.listViewState.dateFilter = action.payload;
      // Reset to first page when filter changes
      state.listViewState.currentPage = 1;
    },
    /**
     * Updates the list view state with provided values
     */
    updateListViewState: (
      state,
      action: PayloadAction<Partial<ListViewState>>
    ) => {
      state.listViewState = { ...state.listViewState, ...action.payload };
    },
    /**
     * Sets the initialization status
     */
    setInitializationStatus: (
      state,
      action: PayloadAction<"initialized" | "initializing" | "not-initialized">
    ) => {
      state.initializationStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch session cases
    builder
      .addCase(fetchSessionById.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
        state.lastError = undefined;
      })
      .addCase(
        fetchSessionById.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const existingIndex = state.sessions.findIndex(
            (session) => session.id === action.payload.id
          );
          if (existingIndex >= 0) {
            state.sessions[existingIndex] = action.payload;
          } else {
            state.sessions.push(action.payload);
          }
          // Always set as currentSession when fetching by ID (typically for edit mode)
          state.currentSession = action.payload;
        }
      )
      .addCase(fetchSessionById.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to fetch session";
        state.lastError = error;
      });

    // Create session cases
    builder
      .addCase(createSession.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
        state.lastError = undefined;
      })
      .addCase(
        createSession.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;

          // Set as currentSession after creation
          state.currentSession = action.payload;
          state.sessions.push(action.payload);

          const newSession = action.payload;
          const now = new Date();
          const sessionStartTime = new Date(newSession.dateStart);

          if (sessionStartTime > now) {
            const currentUpcomingSessions = [
              ...state.upcomingSessionsState.upcomingSessions,
            ];

            const existingIndex = currentUpcomingSessions.findIndex(
              (session) => session.id === newSession.id
            );

            if (existingIndex === -1) {
              currentUpcomingSessions.push(newSession);

              currentUpcomingSessions.sort(
                (a, b) =>
                  new Date(a.dateStart).getTime() -
                  new Date(b.dateStart).getTime()
              );

              if (state.upcomingSessionsState.upcomingSessions.length > 0) {
                const firstSessionTime = new Date(
                  currentUpcomingSessions[0].dateStart
                );
                const lastSessionTime = new Date(
                  currentUpcomingSessions[
                    currentUpcomingSessions.length - 1
                  ].dateStart
                );

                const sessionFitsInRange =
                  sessionStartTime >= firstSessionTime &&
                  sessionStartTime <= lastSessionTime;

                if (
                  sessionFitsInRange ||
                  currentUpcomingSessions.length <= MAX_DISPLAYED_SESSIONS
                ) {
                  state.upcomingSessionsState.upcomingSessions =
                    currentUpcomingSessions.slice(0, MAX_DISPLAYED_SESSIONS);
                }
              } else {
                state.upcomingSessionsState.upcomingSessions =
                  currentUpcomingSessions.slice(0, MAX_DISPLAYED_SESSIONS);
              }
            }
          }
        }
      )
      .addCase(createSession.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to create session";
        state.lastError = error;
      });

    // Update session cases
    builder
      .addCase(updateSession.pending, (state) => {
        state.statusMessage = undefined;
        state.lastError = undefined;
      })
      .addCase(
        updateSession.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.statusMessage = undefined;
          const index = state.sessions.findIndex(
            (session) => session.id === action.payload.id
          );
          if (index >= 0) {
            state.sessions[index] = action.payload;
          }
          // Update currentSession if it's the same session
          if (
            state.currentSession &&
            state.currentSession.id === action.payload.id
          ) {
            state.currentSession = action.payload;
          }
        }
      )
      .addCase(updateSession.rejected, (state, action) => {
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to update session";
        state.lastError = error;
      });

    // Add invitee to session cases
    builder
      .addCase(addInviteeToSession.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        addInviteeToSession.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const index = state.sessions.findIndex(
            (session) => session.id === action.payload.id
          );
          if (index >= 0) {
            state.sessions[index] = action.payload;
          }
        }
      )
      .addCase(addInviteeToSession.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) || "Failed to add invitee to session";
      });

    // Remove invitee from session cases
    builder
      .addCase(removeInviteeFromSession.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        removeInviteeFromSession.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const index = state.sessions.findIndex(
            (session) => session.id === action.payload.id
          );
          if (index >= 0) {
            state.sessions[index] = action.payload;
          }
        }
      )
      .addCase(removeInviteeFromSession.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) || "Failed to remove invitee from session";
      });
    builder
      .addCase(fetchSessionsPaginated.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
        if (state.initializationStatus === "not-initialized") {
          state.initializationStatus = "initializing";
        }
      })
      .addCase(
        fetchSessionsPaginated.fulfilled,
        (state, action: PayloadAction<PaginatedResult<Session>>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.sessions = action.payload.items;
          state.pagination = {
            page: action.payload.page,
            pageSize: action.payload.pageSize,
            totalCount: action.payload.totalCount,
            totalPages: action.payload.totalPages,
            hasPreviousPage: action.payload.hasPreviousPage,
            hasNextPage: action.payload.hasNextPage,
          };
          if (state.initializationStatus === "initializing") {
            state.initializationStatus = "initialized";
          }
        }
      )
      .addCase(fetchSessionsPaginated.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) || "Failed to fetch sessions";
        if (state.initializationStatus === "initializing") {
          state.initializationStatus = "not-initialized";
        }
      });

    builder
      .addCase(fetchSessionsByDateRangePaginated.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
        if (state.initializationStatus === "not-initialized") {
          state.initializationStatus = "initializing";
        }
      })
      .addCase(
        fetchSessionsByDateRangePaginated.fulfilled,
        (state, action: PayloadAction<PaginatedResult<Session>>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.sessions = action.payload.items;
          state.pagination = {
            page: action.payload.page,
            pageSize: action.payload.pageSize,
            totalCount: action.payload.totalCount,
            totalPages: action.payload.totalPages,
            hasPreviousPage: action.payload.hasPreviousPage,
            hasNextPage: action.payload.hasNextPage,
          };
          if (state.initializationStatus === "initializing") {
            state.initializationStatus = "initialized";
          }
        }
      )
      .addCase(fetchSessionsByDateRangePaginated.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) ||
          "Failed to fetch sessions by date range";
        if (state.initializationStatus === "initializing") {
          state.initializationStatus = "not-initialized";
        }
      });

    // Fetch available time slots cases
    builder
      .addCase(fetchAvailableTimeSlotsForNextWeek.pending, (state) => {
        state.timeSlots.initializationStatus = "initializing";
        state.timeSlots.statusMessage = undefined;
      })
      .addCase(
        fetchAvailableTimeSlotsForNextWeek.fulfilled,
        (state, action) => {
          state.timeSlots.initializationStatus = "initialized";
          state.timeSlots.statusMessage = undefined;
          state.timeSlots.availableTimeSlots = action.payload;
        }
      )
      .addCase(
        fetchAvailableTimeSlotsForNextWeek.rejected,
        (state, action) => {
          state.timeSlots.initializationStatus = "failed";
          state.timeSlots.statusMessage =
            (action.payload as string) ||
            "Failed to fetch available time slots";
        }
      );

    // Fetch upcoming sessions cases
    builder
      .addCase(fetchUpcomingSessions.pending, (state) => {
        state.upcomingSessionsState.loadingStatus = LoadingStatus.Loading;
        state.upcomingSessionsState.initializationStatus = "initializing";
      })
      .addCase(
        fetchUpcomingSessions.fulfilled,
        (state, action: PayloadAction<Session[]>) => {
          state.upcomingSessionsState.loadingStatus = LoadingStatus.Idle;
          state.upcomingSessionsState.initializationStatus = "initialized";
          state.statusMessage = undefined;

          // Replace all upcoming sessions with the new data to ensure freshness
          state.upcomingSessionsState.upcomingSessions = action.payload;
        }
      )
      .addCase(fetchUpcomingSessions.rejected, (state) => {
        state.upcomingSessionsState.loadingStatus = LoadingStatus.Failed;
        state.upcomingSessionsState.initializationStatus = "failed";
      });

    // Fetch recently completed sessions cases
    builder
      .addCase(fetchRecentlyCompletedSessions.pending, (state) => {
        state.recentlyCompletedSessionsState.loadingStatus =
          LoadingStatus.Loading;
        state.recentlyCompletedSessionsState.initializationStatus =
          "initializing";
        state.statusMessage = undefined;
      })
      .addCase(
        fetchRecentlyCompletedSessions.fulfilled,
        (state, action: PayloadAction<Session[]>) => {
          state.recentlyCompletedSessionsState.loadingStatus =
            LoadingStatus.Idle;
          state.recentlyCompletedSessionsState.initializationStatus =
            "initialized";
          state.statusMessage = undefined;

          // Replace all recently completed sessions with the new data to ensure freshness
          state.recentlyCompletedSessionsState.recentlyCompletedSessions =
            action.payload;
        }
      )
      .addCase(fetchRecentlyCompletedSessions.rejected, (state, action) => {
        state.recentlyCompletedSessionsState.loadingStatus =
          LoadingStatus.Failed;
        state.recentlyCompletedSessionsState.initializationStatus =
          "initialized";
        state.statusMessage =
          (action.payload as string) ||
          "Failed to fetch recently completed sessions";
      });
  },
});

export const { actions, reducer } = sessionsSlice;
export const {
  clearError,
  resetSessions,
  setCurrentSession,
  clearCurrentSession,
  setSelectedTab,
  setCurrentPage,
  setDateFilter,
  updateListViewState,
} = actions;

// Selectors for accessing sessions state
export const selectSessions = (state: { sessions: SessionsState }) =>
  state.sessions.sessions;
export const selectSessionsStatus = (state: { sessions: SessionsState }) =>
  state.sessions.status;
export const selectSessionsPagination = (state: { sessions: SessionsState }) =>
  state.sessions.pagination;
export const selectCurrentSession = (state: { sessions: SessionsState }) =>
  state.sessions.currentSession;
export const selectSessionsInitializationStatus = (state: {
  sessions: SessionsState;
}) => state.sessions.initializationStatus;

// Selectors for list view state
export const selectSelectedTab = (state: { sessions: SessionsState }) =>
  state.sessions.listViewState.selectedTab;
export const selectCurrentPage = (state: { sessions: SessionsState }) =>
  state.sessions.listViewState.currentPage;
export const selectDateFilter = (state: { sessions: SessionsState }) =>
  state.sessions.listViewState.dateFilter;
export const selectListViewState = (state: { sessions: SessionsState }) =>
  state.sessions.listViewState;
export const selectSessionsStatusMessage = (state: {
  sessions: SessionsState;
}) => state.sessions.statusMessage;
export const selectSessionsLastError = (state: { sessions: SessionsState }) =>
  state.sessions.lastError;

export default reducer;
